import React, { useState, useRef, useEffect } from 'react';
import {
    Smartphone, Sparkles, Loader2, Check, AlertCircle, Play, Save,
    ChevronLeft, ChevronRight, Settings, Pencil, Trash2, ZoomIn, X,
    Eye, EyeOff, Lock, Unlock, CheckCircle, RefreshCw, ImagePlus, Filter,
    Monitor, RotateCcw, ClipboardList
} from 'lucide-react';
import ParticipantModePanel from './ParticipantModePanel';
import LiveResultsPanel from './LiveResultsPanel';
import { JoinPagePreview } from '../join/JoinPage';
import Tooltip from '../../components/ui/Tooltip';
import { API_PATH, getToken, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';
import styles from './ActivityLayout.module.css';

const API_BASE = API_PATH;

const uploadImageFile = async (file, onImageChange, setUploading) => {
    if (!file) return;
    setUploading(true);

    const formData = new FormData();
    formData.append('image', file);

    try {
        const res = await request(`${API_PATH}/upload`, {
            method: 'POST',
            body: formData,
        });

        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        if (data.url && onImageChange) {
            onImageChange(data.url);
        }
    } catch (err) {
        console.error(err);
        alert('Failed to upload image. Please try again.');
    } finally {
        setUploading(false);
    }
};

// ─── Reusable Toggle Switch ───────────────────────────────────────────────────
export const Toggle = ({ checked, onChange, disabled = false }) => (
    <label className={`${styles.toggleLabel} ${disabled ? styles.toggleDisabled : ''}`}>
        <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className={styles.toggleInput} />
        <span className={styles.toggleTrack}>
            <span className={styles.toggleThumb} />
        </span>
    </label>
);

// ─── Settings Group Container ─────────────────────────────────────────────────
export const SettingsGroup = ({ title, children }) => (
    <div className={styles.settingsGroup}>
        
        {children}
    </div>
);

// ─── Single Settings Row ──────────────────────────────────────────────────────
export const SettingsRow = ({ label, description, children, bordered = true }) => (
    <div className={`${styles.settingsRow} ${bordered ? styles.settingsRowBordered : ''}`}>
        <div className={styles.settingsRowText}>
            <div className={styles.rowLabel}>{label}</div>
            {description && <div className={styles.rowDesc}>{description}</div>}
        </div>
        <div className={styles.settingsRowControl}>{children}</div>
    </div>
);

// ─── Sub-row (indented, shown when toggle is on) ──────────────────────────────
export const SubRow = ({ children }) => (
    <div className={styles.subRow}>{children}</div>
);

// ─── Inline textbox ──────────────────────────────────────────────────────────
export const InlineInput = ({ value, onChange, placeholder, type = 'text', min, max }) => (
    <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        max={max}
        className={styles.inlineInput}
    />
);

// ─── Inline Textarea ──────────────────────────────────────────────────────────
export const InlineTextarea = ({ value, onChange, placeholder }) => (
    <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={styles.inlineTextarea}
        rows={3}
    />
);

// ─── AI Components ────────────────────────────────────────────────────────
export const AIButton = ({ onClick, loading, label = "Improve with AI" }) => (
    <button
        className={styles.aiBtn}
        type="button"
        onClick={onClick}
        disabled={loading}
    >
        {loading ? <Loader2 size={14} className={styles.spinnerIcon} /> : <Sparkles size={14} />}
        {label}
    </button>
);

/** Show after AI improve so user can revert to previous text. */
export const AIUndoButton = ({ onClick, label = "Undo" }) => (
    <button
        className={styles.aiUndoBtn}
        type="button"
        onClick={onClick}
        title="Revert to previous text"
    >
        <RotateCcw size={14} />
        {label}
    </button>
);

export const AIOptionButton = ({ onClick, loading, label = "Add options with AI" }) => (
    <button
        className={styles.addOptionBtn}
        type="button"
        onClick={onClick}
        disabled={loading}
    >
        {loading ? <Loader2 size={14} className={styles.spinnerIcon} /> : <Sparkles size={14} />}
        {label}
    </button>
);

// ─── Poll Results (toggle: visible / hidden) ──────────────────────────────────
export const PollResults = ({ value, onChange }) => {
    const isVisible = value === 'visible';
    return (
        <div className={`${styles.settingsRow} ${styles.settingsRowBordered}`}>
            <div className={styles.settingsRowText}>
                <div className={styles.rowLabel}>
                    {isVisible ? 'Results visible' : 'Results hidden'}
                </div>
                <div className={styles.rowDesc}>
                    {isVisible
                        ? 'Participants will see results after they vote'
                        : 'Participants will not see results. You can reveal anytime.'}
                </div>
            </div>
            <div className={styles.settingsRowControl}>
                <Toggle
                    checked={isVisible}
                    onChange={(e) => onChange(e.target.checked ? 'visible' : 'hidden')}
                />
            </div>
        </div>
    );
};

// ─── Question Image Preview (with edit/delete/zoom) ─────────────────────────
export const QuestionImage = ({ imageUrl, onImageChange, onImageLoad, alt = 'Question image' }) => {
    const confirm = useConfirm();
    const [uploading, setUploading] = useState(false);
    const [zoomOpen, setZoomOpen] = useState(false);
    const fileInputRef = useRef(null);
    const imgRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        uploadImageFile(file, onImageChange, setUploading);
        if (e.target) e.target.value = '';
    };

    const handleDeleteImage = async () => {
        const ok = await confirm({
            title: 'Delete Image',
            message: 'Delete this image?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        onImageChange?.(null);
    };

    const handleImageLoad = () => {
        if (imgRef.current?.naturalWidth && imgRef.current?.naturalHeight && typeof onImageLoad === 'function') {
            onImageLoad(imgRef.current.naturalWidth, imgRef.current.naturalHeight);
        }
    };

    return (
        <>
            <div className={styles.questionMedia}>
                <img ref={imgRef} src={imageUrl} alt={alt} className={styles.questionMediaImg} onLoad={handleImageLoad} />
                <div className={styles.imageOverlay}>
                    <div className={styles.imageOverlayActions}>
                        <Tooltip title="Edit image" placement="top">
                            <button
                                type="button"
                                className={styles.imageOverlayBtn}
                                onClick={() => fileInputRef.current?.click()}
                                aria-label="Edit image"
                            >
                                <Pencil size={14} />
                            </button>
                        </Tooltip>
                        <Tooltip title="Zoom image" placement="top">
                            <button
                                type="button"
                                className={styles.imageOverlayBtn}
                                onClick={() => setZoomOpen(true)}
                                aria-label="Zoom image"
                            >
                                <ZoomIn size={14} />
                            </button>
                        </Tooltip>
                        <Tooltip title="Delete image" placement="top">
                            <button
                                type="button"
                                className={`${styles.imageOverlayBtn} ${styles.imageOverlayBtnDanger}`}
                                onClick={handleDeleteImage}
                                aria-label="Delete image"
                            >
                                <Trash2 size={14} />
                            </button>
                        </Tooltip>
                    </div>
                    {uploading && <div className={styles.imageOverlayBadge}>Uploading...</div>}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </div>

            {zoomOpen && (
                <div className={styles.imageZoomOverlay} onClick={() => setZoomOpen(false)}>
                    <div className={styles.imageZoomModal} onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className={styles.imageZoomClose}
                            onClick={() => setZoomOpen(false)}
                            aria-label="Close zoom"
                        >
                            <X size={16} />
                        </button>
                        <img src={imageUrl} alt={alt} className={styles.imageZoomImg} />
                    </div>
                </div>
            )}
        </>
    );
};

// ─── Add Image Section (drag-and-drop + click to choose) ──────────────────────
export const AddImageSection = ({ imageUrl, onImageChange }) => {
    const confirm = useConfirm();
    const [uploading, setUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const processFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        uploadImageFile(file, onImageChange, setUploading);
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        processFile(file);
        if (e.target) e.target.value = '';
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer?.files?.[0];
        processFile(file);
    };

    const handleZoneClick = () => {
        if (uploading) return;
        fileInputRef.current?.click();
    };

    const handleRemoveImage = async () => {
        const ok = await confirm({
            title: 'Delete Image',
            message: 'Delete this image?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        if (onImageChange) onImageChange(null);
    };

    if (imageUrl) {
        return (
            <div className={styles.addImageBoxHasImage}>
                <div className={styles.addImagePreviewWrap}>
                    <img src={imageUrl} alt="Poll question asset" className={styles.addImagePreview} />
                </div>
                <button type="button" className={styles.addImageRemoveBtn} onClick={handleRemoveImage}>
                    <Trash2 size={14} />
                    Remove Image
                </button>
            </div>
        );
    }

    return (
        <div
            className={`${styles.addImageDropZone} ${isDragging ? styles.addImageDropZoneActive : ''} ${uploading ? styles.addImageDropZoneUploading : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleZoneClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleZoneClick(); } }}
            aria-label="Add image: drag and drop or click to choose"
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className={styles.addImageInput}
                onChange={handleFileChange}
                aria-hidden
            />
            {uploading ? (
                <>
                    <Loader2 size={28} className={styles.addImageSpinner} aria-hidden />
                    <span className={styles.addImageUploadingText}>Uploading…</span>
                </>
            ) : (
                <>
                    <div className={styles.addImageIconWrap}>
                        <ImagePlus size={28} className={styles.addImageIcon} aria-hidden />
                    </div>
                    <span className={styles.addImageTitle}>Add Image</span>
                    <span className={styles.addImageDesc}>
                        {isDragging ? 'Drop image here' : 'Drag and drop or click to choose'}
                    </span>
                    <span className={styles.addImageHint}>PNG, JPG, WebP up to 10MB</span>
                </>
            )}
        </div>
    );
};

// ─── Main Layout ──────────────────────────────────────────────────────────────
/**
 * ActivityLayout props:
 *  - icon, color, title, eventName  — metadata (color should be a --cat-* token,
 *    e.g. "var(--cat-violet)", matching the same activity type's sidebar icon)
 *  - left     — left panel (editor area) content
 *  - right    — right settings panel content (Optional now, if empty we can skip)
 *  - participantPreview — content rendered inside the phone in Participant Mode
 *  - onSave      — fn() called when Save & Launch is clicked
 *  - isSaving    — bool
 *  - saveStatus  — 'idle' | 'saving' | 'saved' | 'error'
 *  - isLoading   — bool (content hydration in progress)
 *  - event       — event object { id, name } (for launch + live results)
 *  - activity    — activity object { id, type, title, content }
 */
const ActivityLayout = ({
    icon, color, title, eventName,
    left, right, participantPreview,
    onSave, isSaving = false, saveStatus = 'idle', isLoading = false,
    validate,
    onLoadExample,
    event, activity,
    isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev,
    onSettingsUpdated
}) => {
    const [participantMode, setParticipantMode] = useState(() =>
        sessionStorage.getItem('lp_activity_participantMode') === 'true'
    );
    const [participantsCount, setParticipantsCount] = useState(0);
    const [showSettings, setShowSettings] = useState(() =>
        sessionStorage.getItem('lp_activity_showSettings') === 'true'
    );
    const [resultsToolbarOpen, setResultsToolbarOpen] = useState(false);
    const [footerSettings, setFooterSettings] = useState({});
    const [responsesCount, setResponsesCount] = useState(0);
    const [showResults, setShowResults] = useState(false);
    const [isLaunching, setIsLaunching] = useState(false);
    useEffect(() => {
        if (!showResults) setResultsToolbarOpen(false);
    }, [showResults]);

    useEffect(() => {
        sessionStorage.setItem('lp_activity_participantMode', participantMode ? 'true' : 'false');
    }, [participantMode]);
    useEffect(() => {
        sessionStorage.setItem('lp_activity_showSettings', showSettings ? 'true' : 'false');
    }, [showSettings]);

    const [isRefreshingResults, setIsRefreshingResults] = useState(false);
    const [validationError, setValidationError] = useState('');
    const validationTimerRef = useRef(null);

    const runValidation = () => {
        const msg = validate?.();
        if (msg) {
            setValidationError(msg);
            clearTimeout(validationTimerRef.current);
            validationTimerRef.current = setTimeout(() => setValidationError(''), 3500);
            return false;
        }
        setValidationError('');
        return true;
    };

    /* ── Save-then-launch flow ─────────────────────────────────────────── */
    const pendingLaunchRef = useRef(false);
    const [isSavingBeforeLaunch, setIsSavingBeforeLaunch] = useState(false);

    // Watch saveStatus: once the pending save completes, fire the actual launch
    useEffect(() => {
        if (!pendingLaunchRef.current) return;
        if (saveStatus === 'saved') {
            pendingLaunchRef.current = false;
            setIsSavingBeforeLaunch(false);
            setIsLaunching(true);
            Promise.resolve(onLaunch?.(activity?.id)).finally(() => setIsLaunching(false));
        } else if (saveStatus === 'error') {
            pendingLaunchRef.current = false;
            setIsSavingBeforeLaunch(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    const handleSaveBtnClick = () => {
        if (!runValidation()) return;
        onSave?.();
    };

    const handleStopInteraction = () => {
        onStop?.(activity?.id);
    };

    const handleLaunchInteraction = () => {
        if (isLaunching || isSavingBeforeLaunch) return;
        if (eventLaunchExpired) return;
        if (!runValidation()) return;
        // Always save first so the latest content is persisted before going live
        pendingLaunchRef.current = true;
        setIsSavingBeforeLaunch(true);
        onSave?.();
    };

    const handleRefreshResults = () => {
        setIsRefreshingResults(true);
        // Trigger a re-fetch in LiveResultsPanel via key change or callback
        setTimeout(() => setIsRefreshingResults(false), 1000);
    };

    useEffect(() => {
        setFooterSettings(activity?.content?.settings || {});
    }, [activity?.content?.settings]);

    const updateSetting = async (key, value) => {
        if (!event?.id || !activity?.id) return;
        const prevValue = footerSettings[key] ?? activity?.content?.settings?.[key];
        const prevSettings = activity?.content?.settings || {};
        setFooterSettings(prev => ({ ...prev, [key]: value }));

        const newContent = {
            ...(activity.content || {}),
            settings: {
                ...(activity.content?.settings || {}),
                [key]: value,
            }
        };

        onSettingsUpdated?.(activity.id, newContent.settings);

        try {
            const res = await request(`${API_BASE}/events/${event.id}/activities/${activity.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content: newContent }),
            });

            if (!res.ok) throw new Error('Failed to update setting');
        } catch (err) {
            console.error(err);
            setFooterSettings(prev => ({ ...prev, [key]: prevValue }));
            onSettingsUpdated?.(activity.id, prevSettings);
        }
    };

    const pollResults = footerSettings.pollResults ?? activity?.content?.settings?.pollResults ?? 'hidden';
    const showCorrect = footerSettings.showCorrectAnswer ?? activity?.content?.settings?.showCorrectAnswer ?? false;
    const isLocked = footerSettings.isLocked ?? activity?.content?.settings?.isLocked ?? false;

    const activityWithSettings = activity ? {
        ...activity,
        content: {
            ...(activity.content || {}),
            settings: {
                ...(activity.content?.settings || {}),
                ...footerSettings,
            }
        }
    } : activity;

    // Check validation state at render time (pure function, no side effects)
    const isInvalid = validate ? (validate() !== null) : false;
    const eventLaunchExpired = (() => {
        if (!event?.end_date) return false;
        const end = new Date(event.end_date);
        end.setHours(23, 59, 59, 999);
        return new Date() > end;
    })();
    const launchBlockReason = eventLaunchExpired
        ? 'Event duration has expired. Extend event end date to start polls again.'
        : (validate?.() || 'Fill in the question first');

    const saveBtnClass = `${styles.saveBtn} ${saveStatus === 'saved' ? styles.saveBtnSaved :
        saveStatus === 'error' ? styles.saveBtnError : ''
        }`;

    const saveBtnLabel = () => {
        if (isSaving) return <><Loader2 size={14} className={styles.spinnerIcon} /> Saving...</>;
        if (saveStatus === 'saved') return <><Check size={14} /> Saved and Ready</>;
        if (saveStatus === 'error') return <><AlertCircle size={14} /> Retry Save</>;
        return <><Save size={14} /> Save</>;
    };

    // Unified view structure
    return (
        <div className={styles.paneContent}>
            {/* Top header for the pane */}
            <div className={styles.paneHeader}>
                <div className={styles.paneTitleWrap}>
                    <div className={styles.topIcon} style={{ '--icon-color': color, color }}>
                        {icon}
                    </div>
                    <div>
                        <div className={styles.topTitle}>{activity?.title || title}</div>
                        {isLaunched ? (
                            <div className={styles.liveMeta}>
                                <span className={styles.liveIndicator}>
                                    <span className={styles.liveIndicatorDot} />
                                    Live
                                </span>
                                <span className={styles.liveParticipants}>{participantsCount} online</span>
                                <span className={styles.topMetaText}>Participants are responding in real time</span>
                            </div>
                        ) : showResults ? (
                            <div className={styles.statusMeta}>
                                <span className={styles.statusChip} data-status="results">
                                    <Eye size={10} />
                                    Results
                                </span>
                                <span className={styles.topMetaText}>
                                    {responsesCount} response{responsesCount !== 1 ? 's' : ''} collected
                                </span>
                            </div>
                        ) : (
                            <div className={styles.statusMeta}>
                                <span className={styles.topMetaText}>Refine your interaction, then save and launch</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.paneActions}>
                    {onLoadExample && !isLaunched && !showResults && (
                        <>
                            <Tooltip title="Fill all fields with realistic sample data to see how this poll works" placement="bottom">
                                <button
                                    type="button"
                                    className={styles.loadExampleBtn}
                                    onClick={onLoadExample}
                                >
                                    <ClipboardList size={13} />
                                    Load example
                                </button>
                            </Tooltip>
                            <div className={styles.paneActionsDivider} />
                        </>
                    )}
                    {/* View Results button */}
                    {!isLaunched && responsesCount > 0 && (
                        <Tooltip title={showResults ? 'Back to Edit' : 'View Results'} placement="bottom">
                            <button
                                className={`${styles.participantBtn} ${showResults ? styles.participantBtnActive : ''}`}
                                onClick={() => setShowResults(v => !v)}
                            >
                                <Eye size={15} />
                                <span className={styles.participantBtnText}>{showResults ? 'Edit Poll' : 'View Results'}</span>
                            </button>
                        </Tooltip>
                    )}

                    {/* Results utilities (results view only) */}
                    {showResults && !isLaunched && (
                        <Tooltip title="Refresh results from server" placement="bottom">
                            <button
                                className={styles.refreshBtn}
                                onClick={handleRefreshResults}
                                disabled={isRefreshingResults}
                            >
                                <RefreshCw size={15} className={isRefreshingResults ? styles.spinner : ''} />
                                <span className={styles.participantBtnText}>Refresh</span>
                            </button>
                        </Tooltip>
                    )}

                    {showResults && !isLaunched && (
                        <Tooltip title={resultsToolbarOpen ? 'Hide filter & export' : 'Filter & export'} placement="bottom">
                            <button
                                type="button"
                                className={`${styles.participantBtn} ${resultsToolbarOpen ? styles.participantBtnActive : ''}`}
                                onClick={() => setResultsToolbarOpen(o => !o)}
                                aria-label={resultsToolbarOpen ? 'Hide filter and export' : 'Filter and export'}
                            >
                                <Filter size={15} />
                             
                            </button>
                        </Tooltip>
                    )}

                    {/* Participant mode toggle */}
                    <Tooltip title={participantMode ? 'Close Participant mode' : 'Open Participant mode'} placement="bottom">
                        <button
                            className={`${styles.participantBtn} ${participantMode ? styles.participantBtnActive : ''}`}
                            onClick={() => {
                                setParticipantMode(v => !v);
                                if (!participantMode) setShowSettings(false);
                            }}
                            aria-label={participantMode ? 'Close Participant mode' : 'Open Participant mode'}
                            aria-pressed={participantMode}
                        >
                            <Smartphone size={15} />

                        </button>
                    </Tooltip>

                    {right && !isLaunched && !showResults && (
                        <Tooltip title={showSettings ? 'Close Poll Settings' : 'Open Poll Settings'} placement="bottom">
                            <button
                                className={`${styles.settingsBtn} ${showSettings ? styles.settingsBtnActive : ''}`}
                                onClick={() => {
                                    setShowSettings(v => !v);
                                    if (!showSettings) setParticipantMode(false);
                                }}
                                aria-label={showSettings ? 'Close Poll Settings' : 'Open Poll Settings'}
                                aria-pressed={showSettings}
                            >
                                <Settings size={15} />

                            </button>
                        </Tooltip>
                    )}

                    {!isLaunched && !showResults && (
                        <Tooltip title="Save your changes" placement="bottom">
                            <button
                                className={saveBtnClass}
                                onClick={handleSaveBtnClick}
                                disabled={isSaving || saveStatus === 'saved'}
                            >
                                {saveBtnLabel()}
                            </button>
                        </Tooltip>
                    )}
                    {!isLaunched && (
                            <Tooltip
                                title={isInvalid || eventLaunchExpired ? launchBlockReason : 'Saves and launches this interaction'}
                                placement="top"
                            >
                                <span style={{ display: 'inline-flex' }}>
                                    <button
                                        className={`${styles.launchBtn} ${(isInvalid || eventLaunchExpired) ? styles.launchBtnDisabled : ''}`}
                                        onClick={handleLaunchInteraction}
                                        disabled={isSaving || isLaunching || isSavingBeforeLaunch || isInvalid || eventLaunchExpired}
                                    >
                                        {isSavingBeforeLaunch ? (
                                            <>
                                                <Loader2 size={14} className={styles.spinnerIcon} /> Saving...
                                            </>
                                        ) : isLaunching ? (
                                            <>
                                                <Loader2 size={14} className={styles.spinnerIcon} /> Launching...
                                            </>
                                        ) : (
                                            <>
                                                <Play size={14} /> Launch
                                            </>
                                        )}
                                    </button>
                                </span>
                            </Tooltip>
                        )}
                </div>
            </div>

            {/* Validation error banner */}
            {validationError && (
                <div className={styles.validationBanner} role="alert">
                    <AlertCircle size={14} />
                    {validationError}
                </div>
            )}

            {/* Main Body */}
            <div className={styles.mainContainer}>
                {isLaunched || showResults ? (
                    <div className={`${styles.configBody} ${participantMode ? styles.configBodyWithSettings : styles.configBodyFull}`}>
                        <div className={styles.liveResultsContainer}>
                            <LiveResultsPanel
                                isOpen={true}
                                onClose={isLaunched ? handleStopInteraction : () => setShowResults(false)}
                                eventId={event?.id}
                                activity={activityWithSettings}
                                onParticipantsCountUpdate={setParticipantsCount}
                                onResponsesCountUpdate={setResponsesCount}
                                toolbarOpen={resultsToolbarOpen}
                                onToolbarOpenChange={setResultsToolbarOpen}
                            />
                        </div>
                        {participantMode && (
                            <div className={styles.rightPanel}>
                                <ParticipantModePanel
                                    isOpen={true}
                                    embedded
                                    onClose={() => setParticipantMode(false)}
                                    eventName={eventName || event?.name}
                                    eventCode={event?.code}
                                    activityType={title}
                                >
                                    <JoinPagePreview
                                        event={event || { name: eventName || 'Event', code: event?.code }}
                                        activity={isLaunched ? activity : null}
                                        step={isLaunched ? 'activity' : 'waiting'}
                                        name="Preview User"
                                        participantsCount={participantsCount}
                                    />
                                </ParticipantModePanel>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {/* If there is loading state */}
                        {isLoading && (
                            <div className={styles.loadingOverlay}>
                                <Loader2 size={22} className={styles.spinnerIcon} />
                                <span>Loading saved content…</span>
                            </div>
                        )}

                        {/* Split layout inside the pane — right panel for Poll Settings and/or Participant mode */}
                        <div
                            className={`${styles.configBody} ${(right && showSettings) || participantMode ? styles.configBodyWithSettings : styles.configBodyFull}`}
                            style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                        >
                            <div className={styles.leftPanel}>
                                {left}
                            </div>
                            {((right && showSettings) || participantMode) && (
                                <div className={styles.rightPanel}>
                                    {participantMode && (
                                        <div className={right && showSettings ? styles.rightPanelSection : undefined}>
                                        <ParticipantModePanel
                                            isOpen={true}
                                            embedded
                                            onClose={() => setParticipantMode(false)}
                                            eventName={eventName || event?.name}
                                            eventCode={event?.code}
                                            activityType={title}
                                        >
                                            <JoinPagePreview
                                                event={event || { name: eventName || 'Event', code: event?.code }}
                                                activity={isLaunched ? activity : null}
                                                step={isLaunched ? 'activity' : 'waiting'}
                                                name="Preview User"
                                                participantsCount={participantsCount}
                                            />
                                        </ParticipantModePanel>
                                        </div>
                                    )}
                                    {right && showSettings && (
                                        <>
                                            <div className={styles.settingsTitle}>
                                                <Settings size={12} />
                                                Poll Settings
                                            </div>
                                            {right}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Footer Actions */}
            <div className={styles.paneFooter}>
                <div className={styles.footerControls}>
                    <div className={`${styles.footerCount} ${isLaunched ? styles.footerCountActive : ''}`}>
                        <span className={styles.footerCountNum}>{responsesCount}</span>
                        <span className={styles.footerCountLabel}>responses</span>
                    </div>
                    {isLaunched && activity?.id && (
                        <Tooltip title={pollResults === 'hidden' ? 'Show results' : 'Hide results'} placement="top">
                            <button
                                className={`${styles.footerIconBtn} ${pollResults === 'hidden' ? styles.footerIconBtnActive : ''}`}
                                onClick={() => updateSetting('pollResults', pollResults === 'hidden' ? 'visible' : 'hidden')}
                                aria-label={pollResults === 'hidden' ? 'Show results' : 'Hide results'}
                                aria-pressed={pollResults !== 'hidden'}
                            >
                                {pollResults === 'hidden' ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </Tooltip>
                    )}
                    {isLaunched && activity?.id && activity?.type === 'multiple-choice' && (
                        <Tooltip title={showCorrect ? 'Hide correct answer' : 'Show correct answer'} placement="top">
                            <button
                                className={`${styles.footerIconBtn} ${showCorrect ? styles.footerIconBtnActive : ''}`}
                                onClick={() => updateSetting('showCorrectAnswer', !showCorrect)}
                                aria-label={showCorrect ? 'Hide correct answer' : 'Show correct answer'}
                                aria-pressed={showCorrect}
                            >
                                <CheckCircle size={16} />
                            </button>
                        </Tooltip>
                    )}
                    {isLaunched && activity?.id && (
                        <Tooltip title={isLocked ? 'Start voting' : 'Close voting'} placement="top">
                            <button
                                className={`${styles.footerIconBtn} ${isLocked ? styles.footerIconBtnActive : ''}`}
                                onClick={() => updateSetting('isLocked', !isLocked)}
                                aria-label={isLocked ? 'Start voting' : 'Close voting'}
                                aria-pressed={isLocked}
                            >
                                {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
                            </button>
                        </Tooltip>
                    )}
                </div>
                <div className={styles.footerActions}>
                    <div className={styles.footerPrimaryActions}>
                        {!isLaunched && !showResults && (
                            <Tooltip title="Save your changes" placement="top">
                                <button
                                    className={saveBtnClass}
                                    onClick={handleSaveBtnClick}
                                    disabled={isSaving || saveStatus === 'saved'}
                                >
                                    {saveBtnLabel()}
                                </button>
                            </Tooltip>
                        )}
                        {!isLaunched && (
                            <Tooltip
                                title={isInvalid || eventLaunchExpired ? launchBlockReason : 'Saves and launches this interaction'}
                                placement="top"
                            >
                                <span style={{ display: 'inline-flex' }}>
                                    <button
                                        className={`${styles.launchBtn} ${(isInvalid || eventLaunchExpired) ? styles.launchBtnDisabled : ''}`}
                                        onClick={handleLaunchInteraction}
                                        disabled={isSaving || isLaunching || isSavingBeforeLaunch || isInvalid || eventLaunchExpired}
                                    >
                                        {isSavingBeforeLaunch ? (
                                            <>
                                                <Loader2 size={14} className={styles.spinnerIcon} /> Saving...
                                            </>
                                        ) : isLaunching ? (
                                            <>
                                                <Loader2 size={14} className={styles.spinnerIcon} /> Launching...
                                            </>
                                        ) : (
                                            <>
                                                <Play size={14} /> Launch
                                            </>
                                        )}
                                    </button>
                                </span>
                            </Tooltip>
                        )}
                        {isLaunched && hasPrev && (
                            <Tooltip title="Previous interaction" placement="top">
                                <button className={styles.navBtn} onClick={onPrev}>
                                    <ChevronLeft size={16} />
                                    <span className={styles.navBtnText}>Previous</span>
                                </button>
                            </Tooltip>
                        )}
                        {isLaunched && hasNext && (
                            <Tooltip title="Next interaction" placement="top">
                                <button className={styles.navBtn} onClick={onNext}>
                                    <span className={styles.navBtnText}>Next</span>
                                    <ChevronRight size={16} />
                                </button>
                            </Tooltip>
                        )}
                        {isLaunched && activity?.id && (
                            <Tooltip title="Stop live interaction" placement="top">
                                <button className={styles.stopBtn} onClick={handleStopInteraction}>
                                    Stop
                                </button>
                            </Tooltip>
                        )}
                    </div>
                    <div className={styles.footerUtilityActions}>
                        {isLaunched && event?.id && (
                            <Tooltip title="Open in new tab for second screen / projector" placement="top">
                                <button
                                    type="button"
                                    className={styles.presentBtn}
                                    onClick={() => {
                                        const token = getToken();
                                        const path = `/events/${event.id}/present`;
                                        const url = token
                                            ? `${window.location.origin}${path}#token=${encodeURIComponent(token)}`
                                            : `${window.location.origin}${path}`;
                                        window.open(url, '_blank', 'width=1280,height=800,noopener');
                                    }}
                                >
                                    <Monitor size={15} />
                                    <span className={styles.navBtnText}>Present on second screen</span>
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActivityLayout;
